import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { BidangUsahaList, DokumenLegalitas } from '../../components/RegistrationInfo';

// Selama uji coba, Admin DPP boleh memutuskan pengajuan yang belum diverifikasi DPW (tahap DPW tidak diwajibkan).
const STATUS_DAPAT_DIPUTUSKAN = ['Menunggu verifikasi', 'Menunggu verifikasi nasional'];

const ApprovalFinal = () => {
  const { session, userRole } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('registrations')
        .select('*, master_provinces(name), master_cities(name), registration_business_types(master_business_types(name, sort_order))')
        .in('status', STATUS_DAPAT_DIPUTUSKAN)
        .order('created_at', { ascending: true }); // Prioritaskan pendaftar terlama

      if (error) throw error;
      setRegistrations(data || []);
    } catch (error) {
      console.error('Error fetching national approvals:', error);
      alert('Gagal mengambil data pendaftar.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (session) fetchData();
  }, [session]);

  // Otorisasi Mandiri: Blokir selain admin_nasional
  if (userRole !== 'admin_nasional') {
    return <Navigate to="/admin" replace />;
  }

  const generateMembershipNumber = () => {
    const year = new Date().getFullYear().toString().slice(-2);
    const randomDigits = Math.floor(10000 + Math.random() * 90000); // 5 digit angka acak
    return `HEB-${year}-${randomDigits}`;
  };

  const handleApproveFinal = async (id, status) => {
    const pesan = status === 'Menunggu verifikasi'
      ? 'Pengajuan ini belum diverifikasi DPW. Yakin ingin menyetujui secara final langsung? Anggota akan resmi terdaftar.'
      : 'Yakin ingin menyetujui secara final? Anggota akan resmi terdaftar.';
    if (!window.confirm(pesan)) return;

    try {
      setProcessingId(id);
      const newMembershipNumber = generateMembershipNumber();

      // Hanya pengajuan yang masih menunggu yang diubah, supaya pengajuan yang baru dikembalikan DPW tidak tertimpa
      const { data: updated, error } = await supabase
        .from('registrations')
        .update({
          status: 'Disetujui',
          membership_number: newMembershipNumber
        })
        .eq('id', id)
        .in('status', STATUS_DAPAT_DIPUTUSKAN)
        .select('id');

      if (error) throw error;
      if (!updated || updated.length === 0) {
        throw new Error('Pengajuan tidak ditemukan atau statusnya sudah berubah. Muat ulang halaman.');
      }

      // Hapus baris dari UI tanpa refresh
      setRegistrations((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Error final approval:', error);
      alert('Terjadi kesalahan saat menyetujui final data.' + (error.message ? '\n' + error.message : ''));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('PERINGATAN: Yakin ingin MENOLAK permanen pendaftar ini?')) return;

    try {
      setProcessingId(id);
      const { data: updated, error } = await supabase
        .from('registrations')
        .update({ status: 'Ditolak' })
        .eq('id', id)
        .in('status', STATUS_DAPAT_DIPUTUSKAN)
        .select('id');

      if (error) throw error;
      if (!updated || updated.length === 0) {
        throw new Error('Pengajuan tidak ditemukan atau statusnya sudah berubah. Muat ulang halaman.');
      }

      // Hapus baris dari UI tanpa refresh
      setRegistrations((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Terjadi kesalahan saat menolak data.' + (error.message ? '\n' + error.message : ''));
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Approval Final (Tingkat Nasional)</h1>
      <p className="text-gray-600 mb-6">Daftar pesantren yang menunggu keputusan DPP. Pengajuan yang belum diverifikasi DPW juga bisa langsung disetujui atau ditolak.</p>

      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identitas Pesantren</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lokasi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bidang Usaha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badan Hukum dan Dokumen</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catatan Perbaikan</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi Final</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">Memuat data...</td>
                </tr>
              ) : registrations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">Tidak ada pendaftar yang menunggu approval final.</td>
                </tr>
              ) : (
                registrations.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{item.pesantren_name}</div>
                      <div className="text-sm text-gray-500">Penanggung Jawab Pesantren: {item.pic_hebitren_name}</div>
                      <div className="text-xs font-mono text-gray-400 mt-1">Track: {item.tracking_code}</div>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${item.status === 'Menunggu verifikasi' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                        {item.status === 'Menunggu verifikasi' ? 'Belum diverifikasi DPW' : 'Sudah diverifikasi DPW'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{item.master_cities?.name || '-'}</div>
                      <div className="text-sm text-gray-500">{item.master_provinces?.name || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <BidangUsahaList item={item} />
                    </td>
                    <td className="px-6 py-4">
                      <DokumenLegalitas item={item} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={item.notes || 'Tidak ada catatan'}>
                      {item.notes || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-center space-y-2 sm:space-y-0 sm:space-x-2 flex flex-col sm:flex-row justify-center">
                      <button
                        onClick={() => handleApproveFinal(item.id, item.status)}
                        disabled={processingId === item.id}
                        className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {processingId === item.id ? 'Memproses...' : 'Setujui Final'}
                      </button>
                      <button
                        onClick={() => handleReject(item.id)}
                        disabled={processingId === item.id}
                        className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        Tolak
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApprovalFinal;